import { useCallback, useEffect, useRef, useState } from "react";
import { checkKey, toIssues, type CheckIssue, type CheckResponse } from "@/lib/documentCheck";
import type { ReaderBlock } from "@/lib/mobileReader";

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

export interface DocumentCheck {
  /** Null until the first pass for this content has answered. */
  issues: CheckIssue[] | null;
  running: boolean;
  /** The check itself could not run (network, service down): not the document's fault. */
  failed: boolean;
  run: () => void;
}

/** After an edit, wait this long before checking again: Lilia's edits come in bursts. */
const SETTLE_MS = 2500;

/**
 * Check document runs by itself (Olivia, 10 Oct): when the document opens, and
 * again once it has settled after a change. A result is kept for the session
 * against the content it checked, so reopening an unchanged document costs
 * nothing.
 */
export function useDocumentCheck(documentId: string, blocks: readonly ReaderBlock[], fetchWithAuth: Fetch): DocumentCheck {
  const [issues, setIssues] = useState<CheckIssue[] | null>(null);
  const [running, setRunning] = useState(false);
  const [failed, setFailed] = useState(false);
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const inFlight = useRef<AbortController | null>(null);
  const key = blocks.length ? checkKey(documentId, blocks) : null;

  const run = useCallback(async () => {
    inFlight.current?.abort();
    const ctrl = new AbortController();
    inFlight.current = ctrl;
    const checked = blocksRef.current;
    const k = checkKey(documentId, checked);
    setRunning(true);
    setFailed(false);
    try {
      const res = await fetchWithAuth(`/api/latex/${documentId}/validate`, { method: "POST", signal: ctrl.signal });
      if (!res.ok) throw new Error(String(res.status));
      const list = toIssues((await res.json()) as CheckResponse, checked);
      if (ctrl.signal.aborted) return;
      setIssues(list);
      try {
        sessionStorage.setItem(k, JSON.stringify(list));
      } catch {
        // Storage full or blocked: the result still shows, it just isn't kept.
      }
    } catch {
      if (!ctrl.signal.aborted) setFailed(true);
    } finally {
      if (inFlight.current === ctrl) {
        inFlight.current = null;
        setRunning(false);
      }
    }
  }, [documentId, fetchWithAuth]);

  const first = useRef(true);
  useEffect(() => {
    if (!key) return;
    let cached: CheckIssue[] | null = null;
    try {
      const raw = sessionStorage.getItem(key);
      cached = raw ? (JSON.parse(raw) as CheckIssue[]) : null;
    } catch {
      cached = null;
    }
    if (cached) {
      setIssues(cached);
      setFailed(false);
      return;
    }
    const wait = first.current ? 0 : SETTLE_MS;
    first.current = false;
    const t = setTimeout(() => void run(), wait);
    return () => clearTimeout(t);
  }, [key, run]);

  useEffect(() => () => inFlight.current?.abort(), []);

  return { issues, running, failed, run: () => void run() };
}
